<template>
  <AppLayout title="Dashboard">
    <div class="min-h-screen bg-gray-50">
      <!-- Hero Section -->
      <div class="bg-gradient-to-r from-sonnenglas-500 to-sonnenglas-600">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div class="text-center">
            <h1 class="text-4xl font-bold text-white mb-4">
              {{ $t('Willkommen im SONNENGLAS Partner Portal') }}
            </h1>
            <div class="flex justify-center space-x-4">
              <Link
                :href="route('products')"
                class="bg-white text-sonnenglas-600 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {{ $t('Zum Produktkatalog') }}
              </Link>
              <Link
                :href="route('cart')"
                class="btn btn-sonnenglas py-3 px-6"
              >
                {{ $t('Warenkorb') }}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <!-- Dashboard Grid -->
        <div class="space-y-8">
          <!-- News and Order Row -->
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <!-- Recent News Section -->
            <div class="lg:col-span-2">
              <div class="bg-white rounded-lg shadow-lg overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 class="text-xl font-bold text-gray-900">{{ $t('Aktuelle Neuigkeiten') }}</h2>
                  <a
                    href="/news"
                    class="text-sm text-sonnenglas-600 hover:text-sonnenglas-700 font-medium flex items-center"
                  >
                    {{ $t('Alle Neuigkeiten anzeigen') }}
                    <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </a>
                </div>
                <div class="p-6">
                  <div v-if="recentNews && recentNews.length > 0" class="space-y-6">
                    <div
                      v-for="newsItem in recentNews.slice(0, 2)"
                      :key="newsItem.id"
                      class="border-b border-gray-200 last:border-b-0 pb-6 last:pb-0"
                    >
                      <div class="flex items-start justify-between">
                        <div class="flex-1 min-w-0">
                          <h3 class="text-lg font-semibold text-gray-900 mb-2">
                            {{ newsItem.title }}
                          </h3>
                          <p v-if="newsItem.subtitle" class="text-gray-600 text-sm mb-3">
                            {{ newsItem.subtitle }}
                          </p>
                          <div class="flex items-center justify-between">
                            <span class="text-sm text-gray-500">
                              {{ formatDate(newsItem.date) }}
                            </span>
                            <a
                              :href="`/news/${newsItem.id}`"
                              class="text-sm text-sonnenglas-600 hover:text-sonnenglas-700 font-medium flex items-center"
                            >
                              {{ $t('Weiterlesen') }}
                              <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                              </svg>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center py-8 text-gray-500">
                    <p>{{ $t('Keine Neuigkeiten verfügbar') }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Latest Order Section -->
            <div class="lg:col-span-1">
            <div class="bg-white rounded-lg shadow-lg overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 class="text-xl font-bold text-gray-900">{{ $t('Letzte Bestellung') }}</h2>
                <Link
                  :href="route('ordersList')"
                  class="text-sm text-sonnenglas-600 hover:text-sonnenglas-700 font-medium flex items-center"
                >
                  {{ $t('Alle anzeigen') }}
                  <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
              <div class="p-6">
                <div v-if="latestOrder" class="space-y-4">
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ $t('Order') }} #{{ latestOrder.order_number }}</div>
                    <div class="text-sm text-gray-600">{{ formatDate(latestOrder.created_at) }}</div>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ $t('Status') }}:</span>
                    <span :class="getStatusClass(latestOrder.status)" class="px-2 py-1 text-xs font-medium rounded-full">
                      {{ getStatusText(latestOrder.status) }}
                    </span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ $t('Artikel') }}:</span>
                    <span class="text-sm font-medium text-gray-900">{{ latestOrder.item_count }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">{{ $t('Summe') }}:</span>
                    <span class="text-sm font-semibold text-gray-900">
                      {{ formatCurrency(latestOrder.total_amount, latestOrder.currency) }}
                    </span>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-500 text-center py-4">
                  {{ $t('Noch keine Bestellungen') }}
                  <br>
                  <Link :href="route('products')" class="text-sonnenglas-600 hover:text-sonnenglas-700 font-medium">
                    {{ $t('Jetzt einkaufen') }}
                  </Link>
                </div>
              </div>
            </div>
          </div>
          </div>

          <!-- Quick Actions Section -->
          <div>
            <div class="bg-white rounded-lg shadow-lg overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-200">
                <h2 class="text-xl font-bold text-gray-900">{{ $t('Schnellzugriff') }}</h2>
              </div>
              <div class="p-6">
                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <Link
                    :href="route('products')"
                    class="group p-6 border border-gray-200 rounded-lg hover:border-sonnenglas-300 hover:shadow-md transition-all text-center"
                  >
                    <div class="w-12 h-12 bg-sonnenglas-100 rounded-lg mx-auto mb-3 flex items-center justify-center group-hover:bg-sonnenglas-200 transition-colors">
                      <svg class="w-6 h-6 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                      </svg>
                    </div>
                    <h3 class="font-medium text-gray-900">{{ $t('Produkte') }}</h3>
                    <p class="text-sm text-gray-600 mt-1">{{ $t('Katalog durchstöbern') }}</p>
                  </Link>

                  <Link
                    :href="route('cart')"
                    class="group p-6 border border-gray-200 rounded-lg hover:border-sonnenglas-300 hover:shadow-md transition-all text-center"
                  >
                    <div class="w-12 h-12 bg-sonnenglas-100 rounded-lg mx-auto mb-3 flex items-center justify-center group-hover:bg-sonnenglas-200 transition-colors relative">
                      <svg class="w-6 h-6 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13v6a1 1 0 001 1h1m-1-7h10a1 1 0 011 1v6a1 1 0 01-1 1H9a1 1 0 01-1-1v-6z"/>
                      </svg>
                      <span
                        v-if="cartStore.totalItems > 0"
                        class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
                      >
                        {{ cartStore.totalItems }}
                      </span>
                    </div>
                    <h3 class="font-medium text-gray-900">{{ $t('Warenkorb') }}</h3>
                    <p class="text-sm text-gray-600 mt-1">{{ $t('Bestellung verwalten') }}</p>
                  </Link>

                  <Link
                    :href="route('customerAccount')"
                    class="group p-6 border border-gray-200 rounded-lg hover:border-sonnenglas-300 hover:shadow-md transition-all text-center"
                  >
                    <div class="w-12 h-12 bg-sonnenglas-100 rounded-lg mx-auto mb-3 flex items-center justify-center group-hover:bg-sonnenglas-200 transition-colors">
                      <svg class="w-6 h-6 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                      </svg>
                    </div>
                    <h3 class="font-medium text-gray-900">{{ $t('Mein Konto') }}</h3>
                    <p class="text-sm text-gray-600 mt-1">{{ $t('Konto verwalten') }}</p>
                  </Link>

                  <Link
                    :href="route('ordersList')"
                    class="group p-6 border border-gray-200 rounded-lg hover:border-sonnenglas-300 hover:shadow-md transition-all text-center"
                  >
                    <div class="w-12 h-12 bg-sonnenglas-100 rounded-lg mx-auto mb-3 flex items-center justify-center group-hover:bg-sonnenglas-200 transition-colors">
                      <svg class="w-6 h-6 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                      </svg>
                    </div>
                    <h3 class="font-medium text-gray-900">{{ $t('Meine Bestellungen') }}</h3>
                    <p class="text-sm text-gray-600 mt-1">{{ $t('Verlauf einsehen') }}</p>
                  </Link>

                  <a
                    href="/news"
                    class="group p-6 border border-gray-200 rounded-lg hover:border-sonnenglas-300 hover:shadow-md transition-all text-center"
                  >
                    <div class="w-12 h-12 bg-sonnenglas-100 rounded-lg mx-auto mb-3 flex items-center justify-center group-hover:bg-sonnenglas-200 transition-colors">
                      <svg class="w-6 h-6 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                      </svg>
                    </div>
                    <h3 class="font-medium text-gray-900">{{ $t('Neuigkeiten') }}</h3>
                    <p class="text-sm text-gray-600 mt-1">{{ $t('Neueste Updates') }}</p>
                  </a>

                  <Link
                    :href="route('faq')"
                    class="group p-6 border border-gray-200 rounded-lg hover:border-sonnenglas-300 hover:shadow-md transition-all text-center"
                  >
                    <div class="w-12 h-12 bg-sonnenglas-100 rounded-lg mx-auto mb-3 flex items-center justify-center group-hover:bg-sonnenglas-200 transition-colors">
                      <svg class="w-6 h-6 text-sonnenglas-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                    <h3 class="font-medium text-gray-900">{{ $t('Hilfe & FAQ') }}</h3>
                    <p class="text-sm text-gray-600 mt-1">{{ $t('Hilfe erhalten') }}</p>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <!-- FAQ Section -->
          <div v-if="faqItems && faqItems.length > 0">
            <div class="bg-white rounded-lg shadow-lg overflow-hidden">
              <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 class="text-xl font-bold text-gray-900">{{ $t('Häufig gestellte Fragen') }}</h2>
                <Link
                  :href="route('faq')"
                  class="text-sm text-sonnenglas-600 hover:text-sonnenglas-700 font-medium flex items-center"
                >
                  {{ $t('Alle FAQs anzeigen') }}
                  <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div
                    v-for="faq in faqItems"
                    :key="faq.id"
                    class="border border-gray-200 rounded-lg p-4 hover:border-sonnenglas-300 transition-colors"
                  >
                    <h3 class="font-medium text-gray-900 mb-2">{{ faq.question }}</h3>
                    <p class="text-sm text-gray-600 leading-relaxed">
                      {{ truncateContent(faq.answer, 120) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import AppLayout from '@/layouts/AppLayout.vue'
import { useCartStore } from '@/stores/cart'

interface NewsItem {
  id: number
  title: string
  subtitle?: string
  content: string
  date: string
  featured_image_path?: string
}

interface FaqItem {
  id: number
  question: string
  answer: string
  order: number
}

interface OrderItem {
  order_number: string
  created_at: string
  status: string
  total_amount: number
  currency: string
  item_count: number
  is_shipped: boolean
}

interface Props {
  recentNews?: NewsItem[]
  faqItems?: FaqItem[]
  latestOrder?: OrderItem | null
  language: string
}

const props = defineProps<Props>()
const cartStore = useCartStore()


const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const truncateContent = (content: string, maxLength = 150) => {
  if (!content) return ''
  const text = content.replace(/<[^>]*>/g, '')
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

const getImageUrl = (path: string) => {
  return `/api/partner-portal/images/${path}`
}

const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR'
  }).format(amount)
}

const getStatusClass = (status: string) => {
  const statusClasses = {
    'pending': 'bg-blue-100 text-blue-800',
    'processing': 'bg-blue-100 text-blue-800',
    'paid': 'bg-blue-100 text-blue-800',
    'shipped': 'bg-green-100 text-green-800',
    'delivered': 'bg-green-100 text-green-800',
    'cancelled': 'bg-red-100 text-red-800',
  }
  return statusClasses[status] || 'bg-blue-100 text-blue-800'
}

const getStatusText = (status: string) => {
  // All orders that are not shipped/delivered/cancelled should show "In Bearbeitung" (Processing)
  if (status === 'pending' || status === 'processing' || status === 'paid' || !status) {
    return trans('In Bearbeitung')
  }

  const statusTexts: Record<string, string> = {
    'shipped': trans('Versendet'),
    'delivered': trans('Zugestellt'),
    'cancelled': trans('Storniert'),
  }

  return statusTexts[status] || trans('In Bearbeitung')
}
</script>